// Shared field validators for guest/user input forms.
// Used by CompleteGuestInfoPage, AddGuestPage, CreateUserPage and PersonalSettingsPage
// so validation stays consistent across the app.

/** Country code used for Turkish nationality (see shared/countries.ts). */
export const TURKISH_NATIONALITY_CODE = 'TR';

/** True when the given nationality/country code represents Turkey. */
export const isTurkishNationality = (code?: string | null): boolean =>
  (code || '').trim().toUpperCase() === TURKISH_NATIONALITY_CODE;

/** Minimal guest shape used for profile-completeness checks across login/routes. */
export type GuestProfileCompletenessFields = {
  phoneNumber?: string | null;
  birthdate?: string | Date | null;
  nationality?: string | null;
  country?: string | null;
  city?: string | null;
  postCode?: string | null;
  nationalId?: string | null;
  passportId?: string | null;
};

/**
 * True when required guest profile fields are missing.
 * Identity document is nationality-dependent: Turkish nationals need nationalId,
 * everyone else needs passportId.
 */
export const isGuestProfileIncomplete = (
  guest?: GuestProfileCompletenessFields | null,
): boolean => {
  if (!guest) return true;

  const hasIdentityDocument = isTurkishNationality(guest.nationality)
    ? !!guest.nationalId
    : !!guest.passportId;

  return (
    !guest.phoneNumber ||
    !guest.birthdate ||
    !guest.nationality ||
    !guest.country ||
    !guest.city ||
    !guest.postCode ||
    !hasIdentityDocument
  );
};

/**
 * Validate an email address.
 * Intentionally pragmatic (not full RFC 5322): a single @, non-empty local and
 * domain parts, at least one dot in the domain and no whitespace.
 */
export const validateEmail = (value: string): boolean => {
  const v = (value || '').trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

/**
 * Validate a phone number.
 * Optional leading "+", digits only afterwards, no spaces, 7–15 digits (E.164).
 */
export const validatePhone = (value: string): boolean => {
  const v = (value || '').trim();
  if (!v) return false;
  return /^\+?[0-9]{7,15}$/.test(v);
};

/**
 * Validate a Turkish national identity number (T.C. Kimlik No).
 * 11 digits, first digit non-zero, and the two check digits must satisfy the
 * official TCKN algorithm.
 */
export const validateTCKN = (value: string): boolean => {
  const v = (value || '').trim();
  if (!/^[1-9][0-9]{10}$/.test(v)) return false;

  const d = v.split('').map(Number);
  const oddSum = d[0] + d[2] + d[4] + d[6] + d[8]; // 1st,3rd,5th,7th,9th
  const evenSum = d[1] + d[3] + d[5] + d[7]; // 2nd,4th,6th,8th

  // 10th digit check
  const digit10 = ((oddSum * 7 - evenSum) % 10 + 10) % 10;
  if (digit10 !== d[9]) return false;

  // 11th digit check: sum of first 10 digits mod 10
  const sumFirst10 = d.slice(0, 10).reduce((a, b) => a + b, 0);
  if (sumFirst10 % 10 !== d[10]) return false;

  return true;
};

/**
 * Validate a passport number using the ICAO Doc 9303 shape: letters and digits
 * only, 5–9 characters. Passport formats vary too widely between countries (and
 * across issue years within one country) to safely enforce per-country regexes
 * — doing so falsely rejects legitimate documents — so we only enforce this
 * permissive-but-non-garbage shape. The `nationalityCode` argument is accepted
 * for call-site compatibility and future per-country hints but is not used to
 * reject input.
 */
export const validatePassport = (value: string, _nationalityCode?: string | null): boolean => {
  const v = (value || '').trim().toUpperCase();
  if (!v) return false;
  return /^[A-Z0-9]{5,9}$/.test(v);
};
